import React, { useState } from "react";
import { Heart, Share2, Link2 } from "lucide-react";
import { useAuth, useFavorisArticles, useToast } from "@/lib/api";
import { trackEntite } from "@/lib/track";
import "./ArticleActions.css";

// Actions d'un article (cœur favori + menu de partage), partagées entre
// l'accueil et l'index des articles pour un comportement identique. Chaque
// partage / ouverture est journalisé (trackEntite "article").

// Icônes de partage (mêmes que la fiche solution et l'index des articles).
const IC_WA = <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 00-8.6 15l-1.3 4.8 4.9-1.3A10 10 0 1012 2zm0 2a8 8 0 11-4.1 14.9l-.3-.2-2.9.8.8-2.8-.2-.3A8 8 0 0112 4zm4.5 9.6c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1a6.5 6.5 0 01-3.2-2.8c-.2-.4.2-.4.6-1.2.1-.1 0-.3 0-.4l-.7-1.7c-.2-.5-.4-.4-.5-.4h-.5c-.2 0-.4.1-.6.3-.7.7-.9 1.6-.6 2.8.5 1.6 1.6 3 3.2 4 2.2 1.3 3.1 1 3.7.9.5-.1 1.4-.6 1.6-1.1.2-.6.2-1 .1-1.1z"/></svg>;
const IC_X = <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18.9 2h3.3l-7.2 8.3L23.8 22h-6.7l-5.2-6.8L5.9 22H2.6l7.7-8.8L2.2 2H9l4.7 6.2L18.9 2zm-1.2 18h1.8L7.2 3.9H5.3L17.7 20z"/></svg>;
const IC_LI = <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M4.98 3.5A2.5 2.5 0 002.5 6a2.5 2.5 0 105 0 2.5 2.5 0 00-2.52-2.5zM3 8.98h4V21H3zM9 8.98h3.8v1.64h.05c.53-1 1.83-2.05 3.76-2.05 4 0 4.76 2.64 4.76 6.08V21h-4v-5.36c0-1.28 0-2.92-1.78-2.92s-2.05 1.4-2.05 2.83V21H9z"/></svg>;
const IC_FB = <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 10-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0022 12z"/></svg>;
const IC_MAIL = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
const IC_MORE = <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>;

const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

// Cœur de mise en favori d'un article.
export function ArticleFav({ id, onNeedAuth }) {
  const { user } = useAuth();
  const { toggle, isFav } = useFavorisArticles();
  return (
    <button
      className={`art-fav-btn ${isFav(id) ? "on" : ""}`}
      onClick={(e) => { stop(e); if (!user) return onNeedAuth?.(); toggle(id); }}
      aria-label="Sauvegarder"
    >
      <Heart size={13} fill={isFav(id) ? "#F0A500" : "none"} color={isFav(id) ? "#F0A500" : "currentColor"} />
    </button>
  );
}

// Menu de partage d'un article (mêmes applis que la fiche solution).
export function ArticleShare({ id, lien, titre }) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const u = window.location.origin + lien;
  const t = titre || "";
  const openShare = (e, url, cible) => {
    stop(e);
    trackEntite("article", cible, id);
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };
  return (
    <div className="art-share" style={{ position: "relative" }}>
      <button className="ed-share-btn" onClick={(e) => { stop(e); setOpen(!open); }} aria-label="Partager"><Share2 size={14} /></button>
      {open && (
        <>
          <div onClick={(e) => { stop(e); setOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div className="share-row" onClick={stop}>
            <button className="share-btn" title="Copier le lien" onClick={(e) => { stop(e); navigator.clipboard?.writeText(u); trackEntite("article", "partage_lien", id); toast("Lien copié."); setOpen(false); }}><Link2 size={16} /></button>
            <button className="share-btn" title="WhatsApp" onClick={(e) => openShare(e, `https://wa.me/?text=${encodeURIComponent(t + " sur AfriFuté - " + u)}`, "partage_whatsapp")}>{IC_WA}</button>
            <button className="share-btn" title="X" onClick={(e) => openShare(e, `https://twitter.com/intent/tweet?text=${encodeURIComponent(t + " sur AfriFuté")}&url=${encodeURIComponent(u)}`, "partage_x")}>{IC_X}</button>
            <button className="share-btn" title="LinkedIn" onClick={(e) => openShare(e, `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}`, "partage_linkedin")}>{IC_LI}</button>
            <button className="share-btn" title="Facebook" onClick={(e) => openShare(e, `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`, "partage_facebook")}>{IC_FB}</button>
            <button className="share-btn" title="Email" onClick={(e) => { stop(e); trackEntite("article", "partage_email", id); window.location.href = `mailto:?subject=${encodeURIComponent(t + " - AfriFuté")}&body=${encodeURIComponent(u)}`; setOpen(false); }}>{IC_MAIL}</button>
            {typeof navigator !== "undefined" && navigator.share && (
              <button className="share-btn" title="Plus d'options" onClick={(e) => { stop(e); trackEntite("article", "partage_natif", id); navigator.share({ title: t, url: u }).catch(() => {}); setOpen(false); }}>{IC_MORE}</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
